import { create } from "zustand";
import type { WorkshopStatus } from "@/content/workshops";
import type { PhaseStatus } from "@/features/run-sequence/run-sequence-content";
import type { ActivityStatus } from "@/features/missionControl/workshop-panel-data";
import { broadcastSync } from "@/lib/sync-broadcast";
import {
  hydrateStateDoc,
  initialSyncState,
  persistStateDoc,
  pushStateDoc,
  syncStateDoc,
  type StateDocSyncSlice,
} from "@/lib/bff-state-doc";

/**
 * Progress store — persisted facilitator OVERRIDES for workshop / phase
 * / activity status, layered over the static defaults in
 * `src/content/workshops.ts`, `run-sequence-content.ts`, and
 * `workshop-panel-data.ts`.
 *
 * The whole app ships hardcoded statuses (WS4 active, the rest done /
 * upcoming, etc.). R1 makes those genuinely mutable + durable without
 * rewriting the static content: each read-site resolves its status as
 * `override ?? staticDefault` via {@link resolveStatus}, and facilitator
 * controls write the override here.
 *
 * Persistence — the same dual backend as the record stores (see
 * `@/lib/bff-state-doc`): IndexedDB only by default (`xaes-progress`), or a
 * shared JSON blob at `workshops/_program/progress.json` when
 * `VITE_WORKSHOP_BACKEND=bff`. Program progress is the "where are we / what's
 * done" state behind the Mission Control rollup + navigator, so it must be a
 * shared single source of truth across the facilitation team — not per-browser.
 * On the BFF backend the store hydrates from the shared copy, writes through
 * optimistically, and reconciles on the 15s / focus / cross-tab poll
 * (registered in `SYNCED_STORES`); a `updatedAt` stamp gives whole-document
 * last-write-wins so a poll mid-edit can't revert a just-made local change.
 *
 * Keys:
 *   - workshopStatus — keyed by workshop id (`WS4`).
 *   - phaseStatus    — keyed by the globally-unique phase id (`ws4-a2`).
 *   - activityStatus — keyed by {@link activityKey} (`WS4:activity-2`),
 *     because activity ids (e.g. `intro`) repeat across workshops.
 *
 * Fallback-to-static-default is the load-bearing correctness property:
 * an empty override map must leave every surface reading exactly what it
 * read before this store existed (so a pre-hydration flash is graceful,
 * never broken).
 */

type WorkshopStatusMap = Record<string, WorkshopStatus>;
type PhaseStatusMap = Record<string, PhaseStatus>;
type ActivityStatusMap = Record<string, ActivityStatus>;

/** The persisted document shape (IDB cache + shared blob). */
type ProgressDoc = {
  workshopStatus: WorkshopStatusMap;
  phaseStatus: PhaseStatusMap;
  activityStatus: ActivityStatusMap;
  /** Last-write-wins clock for cross-device reconciliation. `0` on a record
   *  written before this field existed, so any real edit wins over it. */
  updatedAt: number;
};

type ProgressState = ProgressDoc &
  StateDocSyncSlice & {
    isHydrated: boolean;
    /** Set when a BFF hydrate couldn't reach the service (cached copy shown). */
    loadError: string | null;
    hydrate: () => Promise<void>;
    setWorkshopStatus: (workshopId: string, status: WorkshopStatus) => void;
    setPhaseStatus: (phaseId: string, status: PhaseStatus) => void;
    /** Merge several phase overrides in one shot — one state update, one
     *  persist. Used by the Run Sequence toggle (a phase + the auto-
     *  advanced neighbour) and the workshop-level cascade (every phase at
     *  once), so a bulk action is a single write, not N. */
    setPhaseStatuses: (updates: PhaseStatusMap) => void;
    setActivityStatus: (key: string, status: ActivityStatus) => void;
    /** Merge several activity overrides in one shot — one state update,
     *  one persist. Used by the Mission Control activity cascade (the
     *  toggled activity + the re-derived "active" frontier) and the
     *  workshop-level cascade, so a bulk action is a single write. */
    setActivityStatuses: (updates: ActivityStatusMap) => void;
    /** One-time cutover push of the local document to the shared backend
     *  (IDB→BFF migration). Returns the override-entry count pushed, 0 when
     *  the BFF backend is disabled. */
    pushLocalToBff: () => Promise<number>;
  };

const IDB_KEY = "xaes-progress";
const BFF_PATH = "_program/progress.json";

/** Compose the activity-override key. Activity ids repeat across
 *  workshops, so they must be namespaced by workshop. */
export function activityKey(workshopId: string, activityId: string): string {
  return `${workshopId}:${activityId}`;
}

/**
 * Resolve an effective status: the persisted override if present, else
 * the static default. The single chokepoint every read-site routes
 * through — keep it dead simple so the fallback can never drift.
 */
export function resolveStatus<T>(
  map: Record<string, T>,
  id: string,
  fallback: T,
): T {
  return map[id] ?? fallback;
}

const WORKSHOP_STATUSES: WorkshopStatus[] = ["done", "active", "locked"];
const PHASE_STATUSES: PhaseStatus[] = [
  "complete",
  "active",
  "upcoming",
  "locked",
];
const ACTIVITY_STATUSES: ActivityStatus[] = ["done", "active", "locked"];

/** Keep only string→valid-enum pairs from a persisted map. */
function sanitiseMap<T extends string>(
  raw: unknown,
  allowed: T[],
): Record<string, T> {
  const out: Record<string, T> = {};
  if (typeof raw !== "object" || raw === null) return out;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "string" && (allowed as string[]).includes(value)) {
      out[key] = value as T;
    }
  }
  return out;
}

/** Parse a raw persisted value (IDB cache or BFF JSON) into a clean doc. */
function parseDoc(raw: unknown): ProgressDoc {
  const obj = (typeof raw === "object" && raw !== null ? raw : {}) as Record<
    string,
    unknown
  >;
  return {
    workshopStatus: sanitiseMap(obj.workshopStatus, WORKSHOP_STATUSES),
    phaseStatus: sanitiseMap(obj.phaseStatus, PHASE_STATUSES),
    activityStatus: sanitiseMap(obj.activityStatus, ACTIVITY_STATUSES),
    updatedAt: typeof obj.updatedAt === "number" ? obj.updatedAt : 0,
  };
}

/** Whole-document recency reconcile: the newer side wins outright. A missing
 *  shared copy keeps local; a tie keeps the shared copy (stable across tabs). */
function mergeDoc(remote: ProgressDoc | null, local: ProgressDoc): ProgressDoc {
  if (!remote) return local;
  return remote.updatedAt >= local.updatedAt ? remote : local;
}

/** Snapshot the persistable doc from current state. */
function docOf(s: ProgressState): ProgressDoc {
  return {
    workshopStatus: s.workshopStatus,
    phaseStatus: s.phaseStatus,
    activityStatus: s.activityStatus,
    updatedAt: s.updatedAt,
  };
}

export const useProgress = create<ProgressState>((set, get) => {
  /** Stamp `updatedAt`, persist (IDB + shared blob), ping sibling tabs. */
  const commit = (patch: Partial<ProgressDoc>): void => {
    const updatedAt = Date.now();
    set({ ...patch, updatedAt } as Partial<ProgressState>);
    persistStateDoc({
      idbKey: IDB_KEY,
      path: BFF_PATH,
      value: docOf(get()),
      parse: parseDoc,
      merge: mergeDoc,
      // A concurrent edit on another device 412'd us; the helper re-pulled and
      // recency-merged. Adopt the winner so this tab shows the resolved state.
      onReconciled: (doc) => set({ ...doc }),
    });
    broadcastSync();
  };

  return {
    workshopStatus: {},
    phaseStatus: {},
    activityStatus: {},
    updatedAt: 0,
    isHydrated: false,
    loadError: null,
    syncState: initialSyncState(),
    lastSyncedAt: null,
    syncError: null,
    hydrate: async () => {
      if (get().isHydrated) return;
      const { value, loadError } = await hydrateStateDoc({
        idbKey: IDB_KEY,
        path: BFF_PATH,
        parse: parseDoc,
        merge: mergeDoc,
      });
      set({ ...value, isHydrated: true, loadError });
    },
    sync: async () => {
      if (get().syncState === "disabled") return;
      set({ syncState: "syncing", syncError: null });
      const { value, state, error } = await syncStateDoc({
        idbKey: IDB_KEY,
        path: BFF_PATH,
        parse: parseDoc,
        merge: mergeDoc,
        local: docOf(get()),
      });
      set({
        ...value,
        isHydrated: true,
        syncState: state,
        syncError: error,
        lastSyncedAt: state === "synced" ? Date.now() : get().lastSyncedAt,
      });
    },
    setWorkshopStatus: (workshopId, status) => {
      commit({ workshopStatus: { ...get().workshopStatus, [workshopId]: status } });
    },
    setPhaseStatus: (phaseId, status) => {
      commit({ phaseStatus: { ...get().phaseStatus, [phaseId]: status } });
    },
    setPhaseStatuses: (updates) => {
      commit({ phaseStatus: { ...get().phaseStatus, ...updates } });
    },
    setActivityStatus: (key, status) => {
      commit({ activityStatus: { ...get().activityStatus, [key]: status } });
    },
    setActivityStatuses: (updates) => {
      commit({ activityStatus: { ...get().activityStatus, ...updates } });
    },
    pushLocalToBff: async () => {
      const pushed = await pushStateDoc(BFF_PATH, docOf(get()), parseDoc, mergeDoc);
      if (!pushed) return 0;
      const s = get();
      return (
        Object.keys(s.workshopStatus).length +
        Object.keys(s.phaseStatus).length +
        Object.keys(s.activityStatus).length
      );
    },
  };
});
